import { createPortal } from "react-dom";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import styled from "styled-components";
import OptionStockControls from "./OptionStockControls";
import api from "../../../../Component/axios_Interceptor/api";
import {
  product_option_load,
} from "../../../../Component/Interface/Live";
import url from "../../../../Component/server";
import { RowView } from "../../../../Component/style/common_style";

export type OptionSelection = Pick<product_option_load, "option1" | "option2">;
type OptionDraft = Omit<product_option_load, "init_cnt"> & { init_cnt: number | "" };
export type OptionBoxListHandle = {
  getOptions: () => OptionSelection[];
  addOptions: (options: OptionSelection[]) => boolean;
  validate: () => boolean;
  flush: () => Promise<boolean>;
};

const OptionBox_list = forwardRef(
  (
    {
      isNotModi,
      isOpen,
      product_seq,
      updateChnage,
      bulkStockControls = false,
      stockEditable = false,
      syncOptionMode = false,
      footerTarget,
    }: {
      isNotModi: boolean;
      isOpen: boolean;
      product_seq: number;
      updateChnage: () => void;
      bulkStockControls?: boolean;
      stockEditable?: boolean;
      syncOptionMode?: boolean;
      footerTarget?: HTMLElement | null;
    },
    ref
  ) => {
    const [list, setList] = useState<OptionDraft[]>([]);
    const [modiList, setModiList] = useState<OptionDraft[]>([]);
    // Pending deletions are saved together with the current edits.
    const [deletedSeqs, setDeletedSeqs] = useState<number[]>([]);
    const [saving, setSaving] = useState(false);
    const [composing, setComposing] = useState(false);
    const pendingSave = useRef<Promise<boolean> | null>(null);
    const saveLatest = useRef<() => Promise<boolean>>(async () => true);
    const [loaded, setLoaded] = useState(false);
    const nextDraftSeq = useRef(0);
    const loadRequest = useRef(0);
    const [message, setMessage] = useState("");
    const [newOption1, setNewOption1] = useState('');
    const [option2Inputs, setOption2Inputs] = useState<Record<number, string>>({});
    const groupQuantities = useRef<Record<string, number>>({});
    const stockLocked = isNotModi && !stockEditable;
    const stockBaseline = useRef<Record<number, { initial: number; remaining: number }>>({});
    const toInitial = (item: OptionDraft, value: number | ""): number | "" => value === "" ? ""
      : stockEditable ? value + (stockBaseline.current[item.seq]?.initial ?? 0) - (stockBaseline.current[item.seq]?.remaining ?? 0) : value;
    const quantityValue = (item: OptionDraft) => item.init_cnt === "" ? ""
      : stockEditable ? item.init_cnt - (stockBaseline.current[item.seq]?.initial ?? 0) + (stockBaseline.current[item.seq]?.remaining ?? 0) : item.init_cnt;

    const get_product_option_info = async () => {
      const request = ++loadRequest.current;
      setLoaded(false);
      try {
        const res = await api.get(url.Communication_url.product_option_info, { params: { product_seq } });
        if (request !== loadRequest.current) return false;
        const options: product_option_load[] = Array.isArray(res.data.options) ? res.data.options : [];
        stockBaseline.current = Object.fromEntries(options.map(option => [option.seq, { initial: Number(option.init_cnt), remaining: Number(option.current_cnt) }]));
        setList(options);
        setModiList([]);
        setDeletedSeqs([]);
        setLoaded(true);
        return true;
      } catch (error: any) {
        if (request !== loadRequest.current) return false;
        if (error?.response?.status === 404) {
          setList([]);
          setModiList([]);
          setDeletedSeqs([]);
          setLoaded(true);
          return true;
        } else {
          setMessage("옵션을 불러오지 못했습니다. 다시 열어주세요.");
          return false;
        }
      }
    };
    useEffect(() => {
      if (isOpen && product_seq) {
        setMessage("");
        setNewOption1(''); setOption2Inputs({}); groupQuantities.current = {};
        get_product_option_info();
      }
      return () => { loadRequest.current++; };
    }, [isOpen, product_seq, stockEditable]);

    const addOptions = (options: OptionSelection[]) => {
      if (!loaded || pendingSave.current || isNotModi) return false;
      const drafts = options.map(option => ({
        ...option, seq: --nextDraftSeq.current, product_seq, init_cnt: groupQuantities.current[option.option1] ?? "" as const, current_cnt: 0,
      }));
      setList(prev => [...prev, ...drafts]);
      setModiList(prev => [...prev, ...drafts]);
      setMessage("");
      return true;
    };

    const setting_option = (idx: number, fields: Partial<OptionDraft>) => {
      if (pendingSave.current || stockLocked || (isNotModi && Object.keys(fields).some(field => field !== 'init_cnt'))) return;
      setMessage("");
      const updatedList = list.map((item, i) =>
        i === idx ? { ...item, ...fields } : item
      );
      const modi_item = updatedList[idx];

      setList(updatedList);
      setModiList((prev) => {
        const isAlreadyModi = prev.some((item) => item.seq === modi_item.seq);
        if (isAlreadyModi) {
          // 기존 수정 업뎃
          return prev.map((item) =>
            item.seq === modi_item.seq ? modi_item : item
          );
        } else {
          // 새로운 수정 추가
          return [...prev, modi_item];
        }
      });
    };

    const updateGroupName = (option1: string, value: string) => {
      if (pendingSave.current || isNotModi) return;
      setMessage("");
      const changed = list.filter(item => item.option1 === option1).map(item => ({ ...item, option1: value }));
      const seqs = new Set(changed.map(item => item.seq));
      setList(previous => previous.map(item => changed.find(change => change.seq === item.seq) || item));
      setModiList(previous => [...previous.filter(item => !seqs.has(item.seq)), ...changed]);
    };

    const applyGroupStock = (option1: string, quantity: number) => {
      if (!loaded || saving || stockLocked || !list.length) return;
      if (!Number.isSafeInteger(quantity) || quantity < 0) { setMessage('재고는 0 이상의 정수로 입력해주세요.'); return; }
      groupQuantities.current[option1] = quantity;
      const updated = list.map(item => item.option1 === option1 ? ({ ...item, init_cnt: toInitial(item, quantity) }) : item);
      setList(updated);
      setModiList(previous => [...previous.filter(item => item.option1 !== option1), ...updated.filter(item => item.option1 === option1)]);
      setMessage("");
    };

    const validate = () => {
      if (!loaded || composing) return false;
      if (modiList.some(item => item.init_cnt === "" || !Number.isSafeInteger(item.init_cnt) || item.init_cnt < 0 || item.init_cnt > 2147483647)) {
        setMessage("재고는 0 이상의 정수로 입력해주세요.");
        return false;
      }
      if (list.some(item => !item.option1.trim())) {
        setMessage("옵션1을 입력해주세요.");
        return false;
      }
      const keys = list.map(item => `${item.option1.trim()}\u0000${item.option2.trim()}`);
      if (new Set(keys).size !== keys.length) {
        setMessage("중복된 옵션 조합이 있습니다.");
        return false;
      }
      return true;
    };
    const apply_API = (): Promise<boolean> => {
      if (pendingSave.current) return pendingSave.current;
      if (!validate()) return Promise.resolve(false);
      if (!syncOptionMode && modiList.length === 0 && deletedSeqs.length === 0) return Promise.resolve(true);
      if (stockLocked) return Promise.resolve(false);
      setSaving(true);
      setMessage("");
      const request = (async () => {
        try {
          const changed = modiList.filter(item => !deletedSeqs.includes(item.seq));
          if (changed.length || deletedSeqs.length || syncOptionMode) {
            await api.post(url.Communication_url.product_option_save, {
              product_seq,
              ...(deletedSeqs.length ? { deleted_seq_list: deletedSeqs } : {}),
              option_list: changed.map(({ seq, option1, option2, init_cnt }) => ({ ...(seq > 0 ? { seq } : {}), option1: option1.trim(), option2: option2.trim(), init_cnt })),
            });
            setModiList([]);
          }
          setDeletedSeqs([]);
          const refreshed = await get_product_option_info();
          updateChnage();
          if (!refreshed) return false;
          setMessage("저장되었습니다.");
          return true;
        } catch (error: any) {
          setMessage(error?.response?.data?.message || "옵션 저장에 실패했습니다. 입력을 확인하고 다시 시도해주세요.");
          return false;
        } finally {
          pendingSave.current = null;
          setSaving(false);
        }
      })();
      pendingSave.current = request;
      return request;
    };
    saveLatest.current = apply_API;

    const delect_product_option = (seq: number) => {
      if (isNotModi) {
        alert("판매/판매노출 상품의 상태는 수정할 수 없습니다.");
        return;
      }
      if (pendingSave.current) return;
      setMessage("");
      setList((prev) => prev.filter((item) => item.seq !== seq));
      setModiList((prev) => prev.filter((item) => item.seq !== seq));
      if (seq > 0) setDeletedSeqs((prev) => [...prev, seq]);
    };

    useImperativeHandle(ref, () => ({
      addOptions,
      getOptions: () => list,
      validate,
      flush: () => saveLatest.current(),
    }));

    const addGroup = () => {
      const name = newOption1.trim();
      if (!name || !loaded || saving || isNotModi) return;
      if (list.some(option => option.option1 === name)) { setMessage('이미 등록된 옵션1입니다.'); return; }
      groupQuantities.current[name] = 1;
      if (addOptions([{ option1: name, option2: '' }])) setNewOption1('');
    };
    const addOption2 = (option1: string) => {
      const first = list.find(option => option.option1 === option1)!;
      const value = (option2Inputs[first.seq] || '').trim();
      if (!value || !loaded || saving || isNotModi) return;
      if (list.some(option => option.option1 === option1 && option.option2 === value)) { setMessage('중복된 옵션 조합이 있습니다.'); return; }
      if (!first.option2 && list.filter(item => item.option1 === option1).length === 1) setting_option(list.indexOf(first), { option2: value });
      else {
        groupQuantities.current[option1] ??= Number(first.init_cnt) || 0;
        if (!addOptions([{ option1, option2: value }])) return;
      }
      setOption2Inputs(previous => ({ ...previous, [first.seq]: '' }));
    };
    const applyOnEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;
      event.preventDefault(); event.stopPropagation();
    };
    const groups = Array.from(new Set(list.map(option => option.option1)));
    const footer = (saving || message) ? <SaveFooter role="status" aria-live="polite">{saving ? "저장 중…" : message}</SaveFooter> : null;
    return <>
      <GroupedList onCompositionStart={() => setComposing(true)} onCompositionEnd={() => setComposing(false)}>
        {!list.length && <NoneData>옵션을 선택해주세요.</NoneData>}
        {groups.map(option1 => <OptionGroup key={list.find(option => option.option1 === option1)!.seq} role="group" aria-label={`${option1} 옵션`}>
          <GroupRow>
            <Option1Column><GroupLabel><span>옵션1</span>
              <DeleteButton type="button" title="옵션1과 포함된 옵션2 전체 삭제" aria-label={`${option1} 옵션1 삭제`} disabled={!loaded || saving || isNotModi} onClick={() => list.filter(item => item.option1 === option1).forEach(item => delect_product_option(item.seq))}><svg aria-hidden="true" viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18" /></svg></DeleteButton></GroupLabel>
              <input aria-label={`${option1} 옵션1`} value={option1} disabled={!loaded || saving || isNotModi}
                onChange={event => updateGroupName(option1, event.target.value)} onKeyDown={applyOnEnter} />
            </Option1Column>
            <Option2Column><OptionColumnHead><span>옵션2</span>
              {bulkStockControls && <OptionStockControls groupName={option1} disabled={!loaded || saving || stockLocked} onApply={quantity => applyGroupStock(option1, quantity)} />}
              </OptionColumnHead><OptionTags aria-label={`${option1} 옵션2 목록`}>
              {list.map((data, idx) => data.option1 === option1 && <OptionTag key={data.seq}>
                <input className="name" aria-label={`${data.option1} 옵션2`} value={data.option2} placeholder="기본" disabled={!loaded || saving || isNotModi}
                  onChange={event => setting_option(idx, { option2: event.target.value })} onKeyDown={applyOnEnter} />
                <input className="quantity" disabled={!loaded || saving || stockLocked} aria-label={`${data.option1}${data.option2 ? `/${data.option2}` : ""} 재고`}
                  type="number" min={0} value={quantityValue(data)} onKeyDown={applyOnEnter} onChange={event => setting_option(idx, { init_cnt: toInitial(data, event.target.value === "" ? "" : Number(event.target.value)) })} />
                <span>개</span>
                <DeleteButton type="button" disabled={!loaded || saving || isNotModi} aria-label={`${data.option1}${data.option2 ? `/${data.option2}` : ""} 삭제`} onClick={() => delect_product_option(data.seq)}>
                  <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18" /></svg>
                </DeleteButton>
              </OptionTag>)}
              <input className="entry" aria-label={`${option1} 옵션2 추가`} placeholder="옵션2 입력 후 Enter" disabled={!loaded || saving || isNotModi}
                value={option2Inputs[list.find(option => option.option1 === option1)!.seq] || ''}
                onChange={event => setOption2Inputs(previous => ({ ...previous, [list.find(option => option.option1 === option1)!.seq]: event.target.value }))}
                onKeyDown={event => { if (event.key === 'Enter' && (!bulkStockControls || (!event.ctrlKey && !event.metaKey)) && !event.nativeEvent.isComposing) { event.preventDefault(); addOption2(option1); } }} />
            </OptionTags></Option2Column>
          </GroupRow>
        </OptionGroup>)}
        <AddGroupRow>
          <input aria-label="새 옵션1" placeholder="옵션1 입력 후 Enter" value={newOption1} disabled={!loaded || saving || isNotModi}
            onChange={event => setNewOption1(event.target.value)} onKeyDown={event => { if (event.key === 'Enter' && (!bulkStockControls || (!event.ctrlKey && !event.metaKey)) && !event.nativeEvent.isComposing) { event.preventDefault(); addGroup(); } }} />
          <Btn type="button" disabled={!loaded || saving || isNotModi || !newOption1.trim()} onClick={addGroup}><svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 5v14M5 12h14" /></svg>옵션 추가</Btn>
        </AddGroupRow>
      </GroupedList>
      {footerTarget ? createPortal(footer, footerTarget) : footer}
    </>;
  }
);
export default OptionBox_list;

const GroupedList = styled.div`display: flex; flex-direction: column; gap: var(--space-3);`;
const OptionGroup = styled.section`padding: var(--space-3) 0; border-bottom: 1px solid var(--color-border-default);`;
const GroupRow = styled.div`
  display: grid; grid-template-columns: minmax(0, 160px) minmax(0, 1fr); gap: var(--space-3); align-items: start;
  label { display: flex; flex-direction: column; gap: var(--space-2); min-width: 0; color: var(--color-text-secondary); }
  input { min-width: 0; height: var(--control-md); padding: 0 var(--space-2); border: 1px solid var(--color-border-default); border-radius: var(--radius-md); background: var(--color-bg-surface); color: var(--color-text-primary); font: inherit; }
  @media (max-width: 640px) { grid-template-columns: minmax(0, 1fr); }
`;
const Option2Column = styled.div`display: flex; flex-direction: column; gap: var(--space-2); min-width: 0;`;
const Option1Column = styled.div`display: flex; flex-direction: column; gap: var(--space-2); min-width: 0;`;
const OptionColumnHead = styled.div`display: flex; flex-wrap: wrap; justify-content: space-between; gap: var(--space-2); align-items: center; min-height: var(--control-sm); color: var(--color-text-secondary); > span { flex-shrink: 0; }`;
const GroupLabel = styled.div`display: flex; align-items: center; gap: var(--space-2); color: var(--color-text-secondary); min-height: var(--control-sm);`;
const OptionTags = styled.div`
  display: flex; flex-wrap: wrap; gap: var(--space-2); min-width: 0;
  input.entry { flex: 0 1 16rem; width: min(100%, 16rem); }
`;
const OptionTag = styled.div`
  display: grid; grid-template-columns: minmax(0, 1fr) 64px auto var(--control-sm); align-items: center; gap: var(--space-2); min-width: 0; width: 100%; box-sizing: border-box;
  input.name { width: 100%; color: var(--color-text-primary); } input.quantity { width: 64px; } span { color: var(--color-text-secondary); }
`;
const DeleteButton = styled.button`
  flex-shrink: 0; width: var(--control-sm); height: var(--control-sm); padding: var(--space-1); border: 0; border-radius: var(--radius-sm);
  background: transparent; color: var(--color-text-secondary); cursor: pointer;
  svg { width: 100%; height: 100%; fill: none; stroke: currentColor; stroke-width: 2; }
  &:focus-visible { outline: 2px solid var(--color-focus-ring); }
`;
const NoneData = styled.div`padding: var(--space-4); text-align: center; color: var(--color-text-secondary);`;
const SaveFooter = styled.div`padding-top: var(--space-3); color: var(--color-text-secondary); font-size: var(--font-size-sm);`;
const Btn = styled.button`
  display: inline-flex; align-items: center; gap: var(--space-1); margin-left: auto; min-height: var(--control-sm); padding: 0 var(--space-4); border: 1px solid var(--color-brand); border-radius: var(--radius-md);
  background: var(--color-brand); color: var(--color-text-on-brand); font: inherit; cursor: pointer;
  svg { width: 16px; height: 16px; flex-shrink: 0; }
  &:disabled { background: var(--color-bg-muted); color: var(--color-text-disabled); border-color: var(--color-border-default); cursor: default; }
`;

const AddGroupRow = styled.div`
  display: flex; gap: var(--space-3); align-items: center;
  > input { flex: 0 1 calc(var(--control-sm) * 8); width: calc(var(--control-sm) * 8); min-width: 0; height: var(--control-md); padding: 0 var(--space-3); font: inherit; border: 1px solid var(--color-border-default); border-radius: var(--radius-md); background: var(--color-bg-surface); color: var(--color-text-primary); }
  > button { margin-left: 0; flex-shrink: 0; }
`;
